import { useEffect, useRef } from 'react';

import { useLiveLessonClickerContext } from '../providers/LiveLessonClickerProvider';
import {
  decideNextAction,
  executeNextAction,
  NextAction,
  STATE_MUTATING_NEXT_ACTIONS,
} from './liveLessonNextAction';

const ADVANCE_KEYS: ReadonlySet<string> = new Set([
  'PageDown',
  ' ',
  'Spacebar',
  'Enter',
  'ArrowRight',
  'ArrowDown',
]);
const BACK_KEYS: ReadonlySet<string> = new Set(['PageUp', 'ArrowLeft', 'ArrowUp']);

// Tab is the standard focus-nav key, so we only treat it as a clicker
// signal when pressed without Ctrl/Alt/Meta (those are reserved for
// browser/OS shortcuts like Ctrl+Tab). Plain Tab advances; Shift+Tab
// goes back — matching how Kensington and similar presenter remotes
// emit it in their non-PageUp/PageDown modes.
function classifyClickerKey(e: KeyboardEvent): 'advance' | 'back' | null {
  if (e.key === 'Tab') {
    if (e.ctrlKey || e.altKey || e.metaKey) return null;
    return e.shiftKey ? 'back' : 'advance';
  }
  if (ADVANCE_KEYS.has(e.key)) return 'advance';
  if (BACK_KEYS.has(e.key)) return 'back';
  return null;
}

export function isSpaceKey(e: KeyboardEvent): boolean {
  return e.key === ' ' || e.key === 'Spacebar';
}

export function isTextInputFocused(): boolean {
  if (typeof document === 'undefined') return false;
  const el = document.activeElement as HTMLElement | null;
  if (!el) return false;
  const tag = el.tagName?.toLowerCase();
  if (tag === 'input' || tag === 'textarea' || tag === 'select') return true;
  if (el.isContentEditable) return true;
  // JSDOM's isContentEditable getter ignores the attribute, so fall back
  // to a direct attribute check (also covers contenteditable="").
  const attr = el.getAttribute?.('contenteditable');
  if (attr !== null && attr !== undefined && attr !== 'false') return true;
  return false;
}

// The first key press that drove the lesson on this page load.
export interface ClickerFirstUse {
  // KeyboardEvent.key, with ' ' spelled out as 'Space'.
  key: string;
  action: NextAction | 'skipBack';
  // False when a script (e.g. a browser extension) dispatched the event.
  isTrusted: boolean;
}

export interface UseClickerControlsArgs {
  enabled: boolean;
  debounceMs?: number;
  onFirstUse?: (press: ClickerFirstUse) => void;
  // Test seam: lets tests inject a deterministic clock without faking
  // timers across the whole module.
  nowFn?: () => number;
}

// Listens for clicker keys (PageDown/PageUp, Space, Enter, ArrowRight/
// ArrowLeft, Tab/Shift+Tab) and routes them through the central
// decideNextAction → executeNextAction pipeline. Reads handlers and
// state from LiveLessonClickerProvider; do not call outside of that
// provider.
export function useClickerControls({
  enabled,
  debounceMs = 500,
  onFirstUse,
  nowFn,
}: UseClickerControlsArgs): void {
  const ctx = useLiveLessonClickerContext();

  // Sentinel value places the first press well outside any debounce
  // window so the very first clicker keystroke always fires.
  const lastFireAtRef = useRef(Number.NEGATIVE_INFINITY);
  const firstUseFiredRef = useRef(false);
  const onFirstUseRef = useRef(onFirstUse);
  useEffect(() => {
    onFirstUseRef.current = onFirstUse;
  }, [onFirstUse]);

  useEffect(() => {
    if (!enabled) return;
    if (!ctx) return;
    if (typeof window === 'undefined') return;

    const now = nowFn ?? (() => Date.now());

    const handler = (e: KeyboardEvent) => {
      // While the read-aloud runs, Space plays/pauses it like any media
      // player (useSpaceTogglesPlayback). Driving the lesson with it here
      // closed the passage on the first press and skipped to the next
      // slide on the second.
      if (isSpaceKey(e) && ctx.stateRef.current.isReadAloudActive) return;

      const reportFirstUse = (action: ClickerFirstUse['action']) => {
        if (firstUseFiredRef.current) return;
        firstUseFiredRef.current = true;
        onFirstUseRef.current?.({
          key: isSpaceKey(e) ? 'Space' : e.key,
          action,
          isTrusted: e.isTrusted,
        });
      };

      // When a response is selected, ArrowLeft/Right browse through responses
      // instead of navigating slides. Other clicker keys (PageDown, Space,
      // Enter) keep their normal strength-review behaviour.
      const isHorizontalArrow = e.key === 'ArrowLeft' || e.key === 'ArrowRight';
      if (isHorizontalArrow && ctx.stateRef.current.isStrengthReviewOpen) {
        if (isTextInputFocused()) return;
        e.preventDefault();
        if (e.repeat) return;
        if (e.key === 'ArrowRight') {
          ctx.handlersRef.current.browseNextResponse?.();
        } else {
          ctx.handlersRef.current.browsePrevResponse?.();
        }
        return;
      }

      // Same treatment for the MC explanation modal — ArrowLeft/Right
      // walks through every option's explanation without closing the
      // modal. PageDown/Space/Enter still close (handled below via the
      // regular decideNextAction → closeMcExplanation route).
      // stopImmediatePropagation prevents the modal's own bubble-phase
      // window listener from also firing — otherwise both would fire
      // and the modal would advance two explanations per press.
      if (isHorizontalArrow && ctx.stateRef.current.isMcExplanationOpen) {
        if (isTextInputFocused()) return;
        e.preventDefault();
        e.stopImmediatePropagation();
        if (e.repeat) return;
        if (e.key === 'ArrowRight') {
          ctx.handlersRef.current.nextMcExplanation?.();
        } else {
          ctx.handlersRef.current.prevMcExplanation?.();
        }
        return;
      }

      const direction = classifyClickerKey(e);
      if (direction === null) return;
      const isAdvance = direction === 'advance';
      const isBack = direction === 'back';

      // Don't steal keystrokes from text editing surfaces.
      if (isTextInputFocused()) return;

      // Ignore auto-repeat from holding the key down.
      if (e.repeat) {
        e.preventDefault();
        return;
      }

      const sinceLast = now() - lastFireAtRef.current;

      if (isAdvance) {
        const action = decideNextAction(ctx.stateRef.current);

        if (action === 'noop') {
          // Consume the key so the page doesn't scroll.
          e.preventDefault();
          return;
        }

        // Drop a press inside the debounce window after a state-mutating
        // action — guards against a teacher's nervous double-tap firing
        // two transitions back-to-back.
        if (STATE_MUTATING_NEXT_ACTIONS.has(action) && sinceLast < debounceMs) {
          e.preventDefault();
          return;
        }

        e.preventDefault();
        lastFireAtRef.current = now();
        reportFirstUse(action);
        executeNextAction(action, ctx.handlersRef.current);
        return;
      }

      // Back
      if (sinceLast < debounceMs) {
        e.preventDefault();
        return;
      }
      e.preventDefault();
      lastFireAtRef.current = now();
      reportFirstUse('skipBack');
      ctx.handlersRef.current.skipBack?.();
    };

    // Capture phase so preventDefault wins over the focused button's
    // synthesized click (e.g. when the end-lesson modal is open and
    // Cancel is focused, we still confirm — that's what we want).
    window.addEventListener('keydown', handler, true);
    return () => window.removeEventListener('keydown', handler, true);
  }, [enabled, debounceMs, ctx, nowFn]);
}
